import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../api";
import { useT, useAffLangInit, translate, useAffLang } from "../i18n";
import LangPicker from "../LangPicker";

export default function AffForgot() {
  const t = useT();
  useAffLangInit();
  const lang = useAffLang((s) => s.lang);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/affiliate/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="dark fixed inset-0 flex items-center justify-center bg-black overflow-hidden"
      data-testid="aff-forgot-screen"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-amber-500/[0.06] blur-3xl" />
        <div className="absolute top-1/2 -right-40 w-[500px] h-[500px] rounded-full bg-emerald-500/[0.05] blur-3xl" />
      </div>
      <form
        onSubmit={submit}
        className="relative z-10 w-full max-w-sm bg-black/60 border border-white/10 backdrop-blur-xl p-8 space-y-5"
        data-testid="aff-forgot-form"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-display text-3xl font-medium text-white tracking-tight">
              Clipra
            </div>
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-white/40 mt-1">
              {t("forgot.subtitle")}
            </div>
          </div>
          <LangPicker compact />
        </div>

        {sent ? (
          <div
            className="space-y-3 text-sm text-white/70"
            data-testid="aff-forgot-sent"
          >
            <p>
              {translate("forgot.sent.body", lang, {
                email: <strong key="e">{email}</strong>,
              })}
            </p>
            <p className="text-xs text-white/40">
              {t("forgot.sent.spam")}{" "}
              <button
                type="button"
                onClick={() => setSent(false)}
                className="text-emerald-400 hover:text-emerald-300 underline"
              >
                {t("forgot.sent.try_again")}
              </button>
              .
            </p>
            <Link
              to="/affiliate/login"
              className="block text-center text-emerald-400 hover:text-emerald-300 text-xs mt-4"
              data-testid="aff-forgot-back-to-login"
            >
              {t("common.back_to_signin")}
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-white/60">{t("forgot.intro")}</p>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.18em] text-white/50 mb-1.5">
                {t("common.email")}
              </label>
              <input
                type="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40"
                placeholder="you@example.com"
                data-testid="aff-forgot-email"
              />
            </div>
            {error && (
              <div
                className="text-sm text-red-400 border-l-2 border-red-500 pl-3 py-1 font-mono"
                data-testid="aff-forgot-error"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-white text-black py-2.5 text-sm font-medium tracking-wide hover:bg-white/90 disabled:opacity-50"
              data-testid="aff-forgot-submit"
            >
              {busy ? t("forgot.busy") : t("forgot.cta")}
            </button>
            <div className="text-center text-xs font-mono text-white/40">
              <Link
                to="/affiliate/login"
                className="text-emerald-400 hover:text-emerald-300"
                data-testid="aff-forgot-to-login"
              >
                {t("common.back_to_signin")}
              </Link>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
